// Dynamic neural network animation with growing dendrites and pulsed activations
import type {
  DendriticNetwork,
  NetworkParams,
  Neuron,
  ActivePulse,
} from "./DendriticNetwork"

interface PropagationEvent {
  sourceNeuronId: string
  targetNeuronId: string
  intensity: number
  delay: number
  timestamp: number
}

declare const THREE: any

export class DendriticAnimation {
  private THREE: any
  private network: DendriticNetwork
  private renderer: any
  private params: NetworkParams
  private clock: any
  private propagationQueue: PropagationEvent[] = []

  constructor(
    THREE: any,
    network: DendriticNetwork,
    renderer: any,
    params: NetworkParams,
    clock: any,
  ) {
    this.THREE = THREE
    this.network = network
    this.renderer = renderer
    this.params = params
    this.clock = clock
  }

  // Dynamic activation trigger that reinforces connections
  public triggerActivation(neuronId: string, intensity: number = 1.0): void {
    const neuron = this.network.getNeuronById(neuronId) as Neuron
    if (!neuron) return

    const currentTime = this.clock.getElapsedTime()

    // Check refractory period
    if (currentTime - neuron.lastFired < neuron.refractoryPeriod / 1000) {
      return
    }

    // Activate neuron with pulsed intensity
    neuron.activation = Math.min(1.0, intensity)
    neuron.lastFired = currentTime
    neuron.lastActivation = currentTime
    neuron.lastActiveTime = Date.now()
    neuron.glowIntensity = intensity * 2 // Bright glow on activation

    // Strengthen dendrites that receive this activation
    this.reinforceDendrites(neuron, intensity)

    // Propagate activation through the network
    this.propagateActivation(neuron, intensity)
  }

  private reinforceDendrites(neuron: Neuron, intensity: number): void {
    // Strengthen active dendritic branches
    for (const branch of neuron.dendrites.branches) {
      if (branch.activity > 0.1) {
        branch.strength = Math.min(1.0, branch.strength + intensity * 0.1)
        branch.activity = Math.min(1.0, branch.activity + intensity * 0.5)
        branch.lastActive = this.clock.getElapsedTime()

        // Trigger potential growth
        if (
          branch.strength > this.params.growthThreshold &&
          Math.random() < this.params.branchGrowthRate * 10
        ) {
          this.growDendriticBranch(neuron, branch)
        }
      }
    }
  }

  private growDendriticBranch(neuron: Neuron, parentBranch: any): void {
    if (neuron.dendrites.branches.length >= this.params.maxNetworkBranches)
      return

    // Create new branch extending from parent
    const lastPoint = parentBranch.points[parentBranch.points.length - 1]
    const growthDirection = parentBranch.growthDirection.clone()

    // Add some randomness to growth direction
    growthDirection
      .add(
        new this.THREE.Vector3(
          (Math.random() - 0.5) * 0.5,
          (Math.random() - 0.5) * 0.5,
          (Math.random() - 0.5) * 0.5,
        ),
      )
      .normalize()

    const newLength =
      this.params.branchLengthRange.min +
      Math.random() *
        (this.params.branchLengthRange.max -
          this.params.branchLengthRange.min) *
        2.0 // Significantly increased length

    const endPoint = lastPoint
      .clone()
      .add(growthDirection.clone().multiplyScalar(newLength))

    const newBranch = {
      id: `branch_${Date.now()}_${Math.random()}`,
      parentBranch: parentBranch.id,
      children: [],
      points: [lastPoint.clone(), endPoint],
      curve: new this.THREE.CatmullRomCurve3([lastPoint.clone(), endPoint]),
      diameter: Math.max(0.3, parentBranch.diameter * 1.1), // Significantly increased diameter
      length: newLength,
      activity: 0.5,
      strength: 0.3,
      lastActive: this.clock.getElapsedTime(),
      branchOrder: parentBranch.branchOrder + 1,
      synapses: [],
      growthDirection: growthDirection,
      isGrowing: true,
    }

    neuron.dendrites.branches.push(newBranch)
    parentBranch.children.push(newBranch.id)

    console.log(`Grew new dendritic branch for neuron ${neuron.id}`)

    // Update renderer to show new branch
    this.renderer.createDendrites()
  }

  private propagateActivation(neuron: Neuron, intensity: number): void {
    // Create bloom-based activation waves instead of moving pulses
    for (const connection of neuron.connections) {
      if (Math.random() < this.params.propagationChance) {
        const targetNeuron = this.network.getNeuronById(
          connection.targetNeuronId,
        )
        if (targetNeuron) {
          // Create bloom effect along connection
          const activationWave = {
            id: `wave_${Date.now()}_${Math.random()}`,
            sourceNeuronId: neuron.id,
            targetNeuronId: connection.targetNeuronId,
            branchId: "",
            progress: 0,
            intensity:
              intensity * connection.weight * this.params.pulseIntensity * 12.0, // Even more significantly increased intensity for larger activations
            position: neuron.position.clone(),
            speed: this.params.conductionSpeed / 15000, // Extremely slow for a very large, lingering bloom
            type: connection.synapseType,
            startTime: this.clock.getElapsedTime(),
          }

          this.network.activePulses.push(activationWave)

          // Brighten the connection for bloom effect
          connection.activationIntensity =
            (connection.activationIntensity || 0) + activationWave.intensity
          connection.lastUsed = this.clock.getElapsedTime()
          connection.usageCount++

          // Strengthen the connection
          connection.weight = Math.min(1.0, connection.weight + 0.005)

          // Delayed activation of target neuron
          this.propagationQueue.push({
            sourceNeuronId: neuron.id,
            targetNeuronId: targetNeuron.id,
            intensity: activationWave.intensity * 1.0,
            delay: connection.delay * 8, // Significantly slower timing for more atmospheric effect and larger spread
            timestamp: Date.now(),
          })
        }
      }
    }
  }

  private processPropagationQueue(): void {
    const currentTime = Date.now()
    for (let i = this.propagationQueue.length - 1; i >= 0; i--) {
      const event = this.propagationQueue[i]
      if (currentTime >= event.timestamp + event.delay) {
        const targetNeuron = this.network.getNeuronById(event.targetNeuronId)
        if (targetNeuron) {
          this.triggerActivation(targetNeuron.id, event.intensity)
        }
        this.propagationQueue.splice(i, 1) // Remove processed event
      }
    }
  }

  public updateNeurons(): void {
    const currentTime = this.clock.getElapsedTime()

    // Process propagation queue
    this.processPropagationQueue()

    for (const neuron of this.network.neurons) {
      // Restore natural decay of activation
      if (neuron.activation > 0) {
        neuron.activation -= this.params.decayRate * 2.0 // Further increased decay rate
        neuron.activation = Math.max(0, neuron.activation)
      }

      // Decay glow intensity
      if (neuron.glowIntensity > 1) {
        neuron.glowIntensity -= this.params.decayRate * 12 // Significantly increased decay rate for glow
        neuron.glowIntensity = Math.max(1, neuron.glowIntensity)
      }

      // Update dendritic activity
      for (const branch of neuron.dendrites.branches) {
        if (branch.activity > 0) {
          branch.activity -= this.params.decayRate * 1.0 // Further increased decay rate for branch activity
          branch.activity = Math.max(0, branch.activity)
        }

        // Decay strength slowly
        if (currentTime - branch.lastActive > 5) {
          branch.strength -= this.params.strengthDecayRate * 1.5 // Further increased strength decay
          branch.strength = Math.max(0.1, branch.strength)
        }
      }
    }
  }

  public updatePulses(): void {
    const currentTime = this.clock.getElapsedTime()

    for (let i = this.network.activePulses.length - 1; i >= 0; i--) {
      const wave = this.network.activePulses[i]

      // Update wave progress (bloom effect progression)
      wave.progress += wave.speed

      if (wave.progress >= 1) {
        // Wave reached target - trigger dendritic bloom
        const targetNeuron = this.network.getNeuronById(wave.targetNeuronId)
        if (targetNeuron) {
          // Activate dendrites with bloom effect
          const activeDendrites = targetNeuron.dendrites.branches.filter(
            (b: any) => Math.random() < 0.4,
          )
          for (const branch of activeDendrites) {
            branch.activity = Math.min(
              1.0,
              branch.activity + wave.intensity * 0.6,
            )
            branch.lastActive = currentTime
          }

          // Create ripple effect in nearby dendrites
          for (const branch of targetNeuron.dendrites.branches) {
            if (Math.random() < 0.2) {
              branch.activity = Math.min(
                1.0,
                branch.activity + wave.intensity * 0.3,
              )
            }
          }
        }

        // Complete the wave
        this.renderer.removePulse(wave)
        this.network.activePulses.splice(i, 1)
      } else {
        // Update wave for bloom visualization
        const sourceNeuron = this.network.getNeuronById(wave.sourceNeuronId)
        const targetNeuron = this.network.getNeuronById(wave.targetNeuronId)

        if (sourceNeuron && targetNeuron) {
          wave.position
            .copy(sourceNeuron.position)
            .lerp(targetNeuron.position, wave.progress)
          this.renderer.updatePulse(wave)
        }
      }
    }
  }

  public generateSpontaneousActivations(): void {
    if (Math.random() < this.params.spontaneousActivationRate) {
      const randomNeuron =
        this.network.neurons[
          Math.floor(Math.random() * this.network.neurons.length)
        ]
      if (randomNeuron) {
        this.triggerActivation(randomNeuron.id, 0.6 + Math.random() * 0.4)
      }
    }
  }

  public reinforceStrongPathways(): void {
    // Find and reinforce frequently used connections
    for (const neuron of this.network.neurons) {
      for (const connection of neuron.connections) {
        if (connection.usageCount > 5 && Math.random() < 0.01) {
          // Trigger burst along strong pathway
          this.triggerActivation(neuron.id, 0.8)
        }
      }
    }
  }

  public onMouseClick(mouse: any, raycaster: any, camera: any): void {
    raycaster.setFromCamera(mouse, camera)

    // Find clicked neuron and trigger strong activation
    const intersects = raycaster.intersectObjects([this.renderer.neuronMesh])
    if (intersects.length > 0) {
      const instanceId = intersects[0].instanceId
      if (
        instanceId !== undefined &&
        instanceId < this.network.neurons.length
      ) {
        const neuron = this.network.neurons[instanceId]
        console.log(`Manual activation of neuron ${neuron.id}`)
        this.triggerActivation(neuron.id, 1.0)

        // Also trigger nearby neurons for cascading effect
        setTimeout(() => {
          for (let i = 0; i < 3; i++) {
            const randomNearby =
              this.network.neurons[
                Math.floor(Math.random() * this.network.neurons.length)
              ]
            if (
              randomNearby &&
              randomNearby.position.distanceTo(neuron.position) < 50
            ) {
              this.triggerActivation(randomNearby.id, 0.7)
            }
          }
        }, 200)
      }
    }
  }

  public manualTrigger(): void {
    // Trigger a dramatic burst - 3-6 neurons, all with max intensity and glow
    const burstSize = 3 + Math.floor(Math.random() * 4) // 3-6 neurons per burst
    for (let i = 0; i < burstSize; i++) {
      setTimeout(() => {
        const randomNeuron =
          this.network.neurons[
            Math.floor(Math.random() * this.network.neurons.length)
          ]
        if (randomNeuron) {
          // Set activation and glow to max for dramatic effect
          randomNeuron.activation = 1.0
          randomNeuron.glowIntensity = 8.0
          randomNeuron.lastFired = this.clock.getElapsedTime()
          randomNeuron.lastActivation = this.clock.getElapsedTime()
          randomNeuron.lastActiveTime = Date.now()
          this.triggerActivation(randomNeuron.id, 1.0)
          // Debug log
          if (
            typeof window !== "undefined" &&
            (window as any).DEBUG_MANUAL_ACTIVATION
          ) {
            console.log(
              "[DEBUG] ManualTrigger: neuron",
              randomNeuron.id,
              "activation set to 1.0",
            )
          }
        }
      }, i * 200) // Spread out over 200ms for visible cascade
    }
  }
}
